export const GD_META_KEY = "_gd_meta";

export type GdPreisart = "festpreis" | "nachweis";

/**
 * Kundenmarkierung im Angebot.
 * - optional: im Angebot in der Gesamtsumme, vor AB-Umwandlung auswählbar
 * - alternativ: nicht in der Gesamtsumme, vor AB-Umwandlung auswählbar
 */
export type GdPositionsart = "optional" | "alternativ";

export const DEFAULT_NACHWEIS_HINWEIS =
  "Die Arbeiten werden auf Nachweis durchgeführt, geschätzte Kosten:";

export const DEFAULT_GEBUCHTE_STUNDEN_HINWEIS =
  "Bereits angefallene Kosten (geleistete Stunden):";

export const POSITIONSART_LABELS: Record<GdPositionsart, string> = {
  optional: "Optional",
  alternativ: "Alternativ",
};

export interface GdPositionMeta {
  interne_notiz?: string;
  struktur_titel_local_id?: string | null;
  struktur_titel_position_id?: number | null;
  /** Fallback, wenn der Titel beim Speichern noch keine DB-ID hat. */
  struktur_titel_sort_index?: number | null;
  /** Gespeicherte Unterpositionen einer zusammengefassten Leistung (für Auflösen). */
  merged_children?: MergedChildSnapshot[];
  /** Intern: Festpreis- vs. Nachweisposition (erscheint nicht im Kundendokument). */
  preisart?: GdPreisart;
  /** Kundensichtbarer Hinweis oberhalb einer Nachweisposition. */
  nachweis_hinweis?: string;
  /** Stabiler Schlüssel für Positionsbilder (überlebt Position-Replace). */
  media_key?: string;
  /**
   * Optionale / alternative Angebotsposition.
   * Optional zählt im Angebot zur Summe, Alternativ nicht.
   * In der AB nur nach Auswahl ohne Markierung.
   */
  positionsart?: GdPositionsart;
  /** Lieferschein: beauftragte Menge aus AB/Angebot. */
  beauftragte_menge?: number;
  /** Lieferschein: gelieferte Menge. */
  geliefert_menge?: number;
  /** Lieferschein: offener Rückstand. */
  rueckstand_menge?: number;
  /** Lieferschein: Rückstand vom Monteur entfernt. */
  rueckstand_entfernt?: boolean;
  /** Verknüpfung zur Timesheet-Materialposition (Rückfrage-Badge / Übernahme). */
  timesheet_material_position_id?: number;
  /**
   * Interner Aufschlag auf die Summe der Unterpositionen einer zusammengefassten Position.
   * Erhöht den EP im Dokument, erscheint selbst nicht als Zeile.
   */
  merged_aufschlag_prozent?: number;
  merged_aufschlag_betrag?: number;
  merged_aufschlag_modus?: "prozent" | "betrag";
}

export interface MergedChildSnapshot {
  position_typ: string;
  bezeichnung: string;
  beschreibung?: string | null;
  artikel_id?: number | null;
  ist_freier_artikel?: boolean;
  hero_nr?: string | null;
  einheit?: string | null;
  menge: number;
  einzelpreis_netto: number;
  rabatt_prozent: number;
  rabatt_betrag?: number;
  rabatt_modus?: "prozent" | "betrag";
  rabatt_ausweis?: "intern" | "dokument";
  steuersatz_prozent?: number;
  kalkulation_json?: unknown;
  interne_notiz?: string;
}

export function readGdMeta(kalkulation: unknown): GdPositionMeta {
  if (!kalkulation || typeof kalkulation !== "object") return {};
  const raw = (kalkulation as Record<string, unknown>)[GD_META_KEY];
  if (!raw || typeof raw !== "object") return {};
  const meta = raw as GdPositionMeta;
  const preisart =
    meta.preisart === "nachweis" || meta.preisart === "festpreis" ? meta.preisart : undefined;
  const positionsart =
    meta.positionsart === "optional" || meta.positionsart === "alternativ"
      ? meta.positionsart
      : undefined;
  const numOrUndef = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) ? v : undefined;

  return {
    interne_notiz: meta.interne_notiz,
    struktur_titel_local_id: meta.struktur_titel_local_id ?? null,
    struktur_titel_position_id: meta.struktur_titel_position_id ?? null,
    struktur_titel_sort_index: meta.struktur_titel_sort_index ?? null,
    merged_children: Array.isArray(meta.merged_children) ? meta.merged_children : undefined,
    preisart,
    nachweis_hinweis: typeof meta.nachweis_hinweis === "string" ? meta.nachweis_hinweis : undefined,
    media_key: typeof meta.media_key === "string" && meta.media_key.trim() ? meta.media_key.trim() : undefined,
    positionsart,
    beauftragte_menge: numOrUndef(meta.beauftragte_menge),
    geliefert_menge: numOrUndef(meta.geliefert_menge),
    rueckstand_menge: numOrUndef(meta.rueckstand_menge),
    rueckstand_entfernt: meta.rueckstand_entfernt === true,
    timesheet_material_position_id: numOrUndef(meta.timesheet_material_position_id),
    merged_aufschlag_prozent: numOrUndef(meta.merged_aufschlag_prozent),
    merged_aufschlag_betrag: numOrUndef(meta.merged_aufschlag_betrag),
    merged_aufschlag_modus:
      meta.merged_aufschlag_modus === "betrag" || meta.merged_aufschlag_modus === "prozent"
        ? meta.merged_aufschlag_modus
        : undefined,
  };
}

export function readLieferscheinDelivery(position: {
  menge?: number | null;
  kalkulation_json?: unknown;
}): {
  beauftragte_menge: number;
  geliefert_menge: number;
  rueckstand_menge: number;
} {
  const meta = readGdMeta(position.kalkulation_json);
  const menge = Number(position.menge ?? 0) || 0;
  const hasMeta =
    meta.beauftragte_menge != null ||
    meta.geliefert_menge != null ||
    meta.rueckstand_menge != null;
  if (!hasMeta) {
    return { beauftragte_menge: menge, geliefert_menge: menge, rueckstand_menge: 0 };
  }
  const beauftragt = meta.beauftragte_menge ?? menge;
  const geliefert = meta.geliefert_menge ?? menge;
  const rueckstand =
    meta.rueckstand_entfernt === true
      ? 0
      : (meta.rueckstand_menge ?? Math.max(0, beauftragt - geliefert));
  return {
    beauftragte_menge: beauftragt,
    geliefert_menge: geliefert,
    rueckstand_menge: rueckstand,
  };
}

export function writeGdMeta(kalkulation: unknown, meta: GdPositionMeta): Record<string, unknown> | null {
  const base =
    kalkulation && typeof kalkulation === "object"
      ? { ...(kalkulation as Record<string, unknown>) }
      : {};
  delete base[GD_META_KEY];

  const hasDeliveryMeta =
    meta.beauftragte_menge != null ||
    meta.geliefert_menge != null ||
    meta.rueckstand_menge != null ||
    meta.rueckstand_entfernt === true;

  const aufschlagProzent =
    typeof meta.merged_aufschlag_prozent === "number" && Number.isFinite(meta.merged_aufschlag_prozent)
      ? meta.merged_aufschlag_prozent
      : undefined;
  const aufschlagBetrag =
    typeof meta.merged_aufschlag_betrag === "number" && Number.isFinite(meta.merged_aufschlag_betrag)
      ? meta.merged_aufschlag_betrag
      : undefined;
  const aufschlagModus =
    meta.merged_aufschlag_modus === "betrag" || meta.merged_aufschlag_modus === "prozent"
      ? meta.merged_aufschlag_modus
      : undefined;
  const hasMergedAufschlag =
    aufschlagModus === "betrag" ||
    (aufschlagProzent != null && aufschlagProzent !== 0) ||
    (aufschlagBetrag != null && aufschlagBetrag !== 0);

  const hasMeta =
    Boolean((meta.interne_notiz || "").trim()) ||
    meta.struktur_titel_local_id != null ||
    meta.struktur_titel_position_id != null ||
    meta.struktur_titel_sort_index != null ||
    (Array.isArray(meta.merged_children) && meta.merged_children.length > 0) ||
    meta.preisart != null ||
    Boolean((meta.nachweis_hinweis || "").trim()) ||
    Boolean((meta.media_key || "").trim()) ||
    meta.positionsart != null ||
    hasDeliveryMeta ||
    (typeof meta.timesheet_material_position_id === "number" &&
      Number.isFinite(meta.timesheet_material_position_id)) ||
    hasMergedAufschlag;

  if (!hasMeta && Object.keys(base).length === 0) return null;
  if (!hasMeta) return Object.keys(base).length ? base : null;

  return {
    ...base,
    [GD_META_KEY]: {
      interne_notiz: meta.interne_notiz || undefined,
      struktur_titel_local_id: meta.struktur_titel_local_id ?? undefined,
      struktur_titel_position_id: meta.struktur_titel_position_id ?? undefined,
      struktur_titel_sort_index: meta.struktur_titel_sort_index ?? undefined,
      merged_children:
        Array.isArray(meta.merged_children) && meta.merged_children.length
          ? meta.merged_children
          : undefined,
      preisart: meta.preisart || undefined,
      nachweis_hinweis: (meta.nachweis_hinweis || "").trim() || undefined,
      media_key: (meta.media_key || "").trim() || undefined,
      positionsart: meta.positionsart || undefined,
      beauftragte_menge:
        typeof meta.beauftragte_menge === "number" && Number.isFinite(meta.beauftragte_menge)
          ? meta.beauftragte_menge
          : undefined,
      geliefert_menge:
        typeof meta.geliefert_menge === "number" && Number.isFinite(meta.geliefert_menge)
          ? meta.geliefert_menge
          : undefined,
      rueckstand_menge:
        typeof meta.rueckstand_menge === "number" && Number.isFinite(meta.rueckstand_menge)
          ? meta.rueckstand_menge
          : undefined,
      rueckstand_entfernt: meta.rueckstand_entfernt === true ? true : undefined,
      timesheet_material_position_id:
        typeof meta.timesheet_material_position_id === "number" &&
        Number.isFinite(meta.timesheet_material_position_id)
          ? meta.timesheet_material_position_id
          : undefined,
      merged_aufschlag_modus: hasMergedAufschlag ? aufschlagModus ?? "prozent" : undefined,
      merged_aufschlag_prozent:
        aufschlagProzent != null && aufschlagProzent !== 0 ? aufschlagProzent : undefined,
      merged_aufschlag_betrag:
        aufschlagBetrag != null && aufschlagBetrag !== 0 ? aufschlagBetrag : undefined,
    },
  };
}

export function stripGdMeta(kalkulation: unknown): Record<string, unknown> | null {
  if (!kalkulation || typeof kalkulation !== "object") return null;
  const base = { ...(kalkulation as Record<string, unknown>) };
  delete base[GD_META_KEY];
  return Object.keys(base).length ? base : null;
}
